import {
  ILineDataInVerticalStackedBarChart,
  IVerticalStackedChartProps,
  VerticalStackedBarChart
} from "@fluentui/react-charting";
import { ApexOptions } from "apexcharts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { CustomShimmer } from "../../../../helper/CustomShimmer";
import CustomCard from "../../../../SharedComponents/CustomCard";
import { selectViewport } from "../../../../slices/UI.slice";
import { useAppSelector } from "../../../../store/hooks";
import { PartnerTenantsChartProps } from "./partnerInfluence.interfaces";

const PartnerTenantsChart = ({
  data,
  height = 400,
  showAvgLine = true,
  groupBars = false,
  combinePartners = false,
  cardTitle = "Partners & Tenants Trend",
  kustoLink,
  colors,
  legends,
  isLoading
}: PartnerTenantsChartProps) => {
  const partnersIsEliteColor = colors?.partnersIsElite ?? "#0078D4"; // primary blue
  const partnersNonEliteColor = colors?.partnersNonElite ?? "#7FB3E8"; // light blue
  const tenantsColor = colors?.tenants ?? "#B7E4C7"; // light green
  const avgColor = colors?.avgCustomers ?? "#E3008C"; // pink
  const partnersIsEliteLegend = legends?.partnersIsElite ?? "Partners IsElite";
  const partnersNonEliteLegend = legends?.partnersNonElite ?? "Partners NonElite";
  const tenantsLegend = legends?.tenants ?? "Tenants";
  const avgCustomersLegend = legends?.avgCustomers ?? "Avg customers / partners";

  const widthCalc = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(500);
  const resizedWidth = useAppSelector((state) => selectViewport(state).offsetWidth);
  const chartWidth = Math.max(width - 10, 0);

  const handleResize = useCallback(setWidth, []);

  useEffect(() => {
    if (widthCalc.current) {
      handleResize(widthCalc.current.offsetWidth);
    }
  }, [handleResize, resizedWidth]);

  // Cap the secondary axis just above the max avg value so the line is visible.
  const maxAvg = useMemo(
    () =>
      Math.max(
        100,
        Math.ceil(
          Math.max(
            ...data.map((d) => Number(d.avgCustomersbyPartners) || 0)
          ) * 1.2
        )
      ),
    [data]
  );

  const stackedData: IVerticalStackedChartProps[] = useMemo(
    () =>
      data.map((d, index) => {
        const lineData: ILineDataInVerticalStackedBarChart[] | undefined = showAvgLine
          ? [
            {
              legend: avgCustomersLegend,
              y: d.avgCustomersbyPartners,
              color: avgColor,
              useSecondaryYScale: true
            }
          ]
          : undefined;
        const dt = new Date(d.snapshotDate);
        const label = Number.isNaN(dt.getTime())
          ? d.snapshotDate
            ? String(d.snapshotDate)
            : `Snapshot ${index + 1}`
          : dt.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
        return {
          xAxisPoint: label,
          chartData: [
            {
              legend: partnersIsEliteLegend,
              data: Number(d.partners_IsElite),
              color: partnersIsEliteColor
            },
            {
              legend: partnersNonEliteLegend,
              data: Number(d.partners_NonElite),
              color: partnersNonEliteColor
            },
            { legend: tenantsLegend, data: Number(d.tenantCount), color: tenantsColor }
          ],
          lineData
        };
      }),
    [
      data,
      partnersIsEliteColor,
      partnersNonEliteColor,
      tenantsColor,
      avgColor,
      showAvgLine,
      partnersIsEliteLegend,
      partnersNonEliteLegend,
      tenantsLegend,
      avgCustomersLegend
    ]
  );

  const hasChartData = stackedData.length > 0;

  const groupedSeries: ApexAxisChartSeries = useMemo(() => {
    const series: ApexAxisChartSeries = combinePartners
      ? [
          {
            name: partnersIsEliteLegend,
            type: "column",
            data: data.map((item) => Number(item.partners_IsElite) + Number(item.partners_NonElite))
          },
          {
            name: tenantsLegend,
            type: "column",
            data: data.map((item) => Number(item.tenantCount))
          }
        ]
      : [
          {
            name: partnersIsEliteLegend,
            type: "column",
            data: data.map((item) => Number(item.partners_IsElite))
          },
          {
            name: partnersNonEliteLegend,
            type: "column",
            data: data.map((item) => Number(item.partners_NonElite))
          },
          {
            name: tenantsLegend,
            type: "column",
            data: data.map((item) => Number(item.tenantCount))
          }
        ];

    if (showAvgLine) {
      series.push({
        name: avgCustomersLegend,
        type: "line",
        data: data.map((item) => Number(item.avgCustomersbyPartners))
      });
    }

    return series;
  }, [data, partnersIsEliteLegend, partnersNonEliteLegend, tenantsLegend, avgCustomersLegend, showAvgLine, combinePartners]);

  const groupedChartOptions: ApexOptions = useMemo(
    () => ({
      chart: {
        animations: { enabled: false },
        fontFamily: "Segoe UI, sans-serif",
        toolbar: { show: false },
        type: "line"
      },
      colors: combinePartners
        ? [partnersIsEliteColor, tenantsColor, avgColor]
        : [partnersIsEliteColor, partnersNonEliteColor, tenantsColor, avgColor],
      dataLabels: { enabled: false },
      grid: {
        borderColor: "#E1DFDD",
        strokeDashArray: 3
      },
      legend: {
        fontSize: "12px",
        horizontalAlign: "center",
        position: "bottom"
      },
      markers: { size: showAvgLine ? 3 : 0 },
      plotOptions: {
        bar: {
          borderRadius: 2,
          columnWidth: "62%"
        }
      },
      stroke: {
        curve: "straight",
        width: showAvgLine
          ? (combinePartners ? [0, 0, 2] : [0, 0, 0, 2])
          : (combinePartners ? [0, 0] : [0, 0, 0])
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (value: number, { seriesIndex }: { seriesIndex: number }) =>
            seriesIndex === (combinePartners ? 2 : 3)
              ? value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              : Math.round(value).toLocaleString("en-US")
        }
      },
      xaxis: {
        categories: data.map((item, index) => {
          const date = new Date(item.snapshotDate);
          return Number.isNaN(date.getTime())
            ? `Snapshot ${index + 1}`
            : date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
        }),
        labels: { rotate: 0 }
      },
      yaxis: showAvgLine
        ? [
            {
              seriesName: combinePartners
                ? [partnersIsEliteLegend, tenantsLegend]
                : [partnersIsEliteLegend, partnersNonEliteLegend, tenantsLegend],
              min: 0,
              labels: {
                formatter: (value: number) => Math.round(value).toLocaleString("en-US")
              }
            },
            {
              seriesName: avgCustomersLegend,
              min: 0,
              max: maxAvg,
              opposite: true,
              labels: {
                formatter: (value: number) => Math.round(value).toLocaleString("en-US")
              }
            }
          ]
        : {
            min: 0,
            labels: {
              formatter: (value: number) => Math.round(value).toLocaleString("en-US")
            }
          }
    }),
    [
      data,
      partnersIsEliteColor,
      partnersNonEliteColor,
      tenantsColor,
      avgColor,
      partnersIsEliteLegend,
      partnersNonEliteLegend,
      tenantsLegend,
      avgCustomersLegend,
      showAvgLine,
      maxAvg,
      combinePartners
    ]
  );

  return (
    <div ref={widthCalc} style={{ width: "100%", minWidth: 0 }}>
      <CustomCard cardHeaderTitle={cardTitle} chartTitle={cardTitle} kustoLink={kustoLink} isLoading={isLoading}>
        <div style={{ height, width: "100%" }}>
          {isLoading ? (
            <CustomShimmer columnCount={4} rowCount={8} styles={{ root: { height } }} />
          ) : hasChartData ? (
            groupBars ? (
              <Chart options={groupedChartOptions} series={groupedSeries} type="line" height={height} width="100%" />
            ) : (
              <VerticalStackedBarChart
                data={stackedData}
                height={height}
                width={chartWidth}
                barWidth={24}
                barCornerRadius={2}
                legendsOverflowText="More"
                yAxisTickCount={5}
                {...(showAvgLine ? { secondaryYScaleOptions: { yMinValue: 0, yMaxValue: maxAvg } } : {})}
              />
            )
          ) : (
            <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center" }}>
              No data available
            </div>
          )}
        </div>
      </CustomCard>
    </div>
  );
};

export default PartnerTenantsChart;
